import axios, { type AxiosInstance } from 'axios';
import {
  AUTH_EXPIRED_MESSAGE_KEY,
  REDIRECT_AFTER_LOGIN_KEY,
  clearAuthToken,
  getAuthToken,
} from '@/lib/auth';
import { clearCreatedLaunch } from '@/features/fit-launcher/lib/createdLaunch';
import { clearManagedLaunch } from '@/features/managed/lib/launchHandover';

const DEFAULT_TIMEOUT_MS = 120_000;
const TIMEOUT_MS = Number(import.meta.env.VITE_API_TIMEOUT ?? DEFAULT_TIMEOUT_MS);

export const apiClient: AxiosInstance = axios.create({
  timeout: TIMEOUT_MS,
});

let isRedirecting = false;

apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;

    // Only HTTP 401 reliably means "your session is invalid — sign in again".
    //
    // Other statuses, even when their `detail` mentions "token" or
    // "authentication", are NOT session errors and must not log the user
    // out. Concrete examples this guard caught accidentally before:
    //   - 500: "Failed to generate download URL: Error when retrieving
    //          token from sso: Token has expired and refresh failed"
    //          (the BACKEND's AWS SSO token, not the user's auth token).
    //   - 5xx from any provider whose error message happens to include
    //     the words "token" / "unauthorized" / "authentication".
    //   - 403 — the user IS authenticated, they just don't have permission
    //     for this specific resource. Surfacing it as "session expired"
    //     mis-blames the user and forces a needless re-login.
    //
    // If the backend ever does want to nudge the client to re-auth on a
    // non-401 status, it should emit 401 explicitly.
    const isAuthError = status === 401;

    if (isAuthError && !isRedirecting) {
      isRedirecting = true;
      clearAuthToken();
      clearCreatedLaunch();
      clearManagedLaunch();
      // With the query, so a deep link such as a buy page's quantity
      // survives; the login page still accepts only safeRedirectPath's pages.
      const { pathname, search } = window.location;
      if (pathname !== '/login' && pathname !== '/') {
        try {
          window.localStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, `${pathname}${search}`);
        } catch {
          /* storage blocked */
        }
      }
      try {
        window.sessionStorage.setItem(
          AUTH_EXPIRED_MESSAGE_KEY,
          'Your session has expired. Please sign in again.',
        );
      } catch {
        /* storage blocked */
      }
      window.location.href = '/login';
    }

    return Promise.reject(error);
  },
);

window.addEventListener('popstate', () => {
  isRedirecting = false;
});
